/**
 * WeekNavigator
 * Componente de navegação entre semanas para visualização semanal de publicações
 *
 * Fase 1.1 do Plano de Evolução - Navegação Temporal
 */

import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Calendar, RotateCcw, Info } from 'lucide-react';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, isSameWeek } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface WeekNavigatorProps {
  currentWeekStart: Date;
  onWeekChange: (weekStart: Date) => void;
}

const WEEK_OPTIONS = { weekStartsOn: 1 } as const; // Monday = 1

function NavButton({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon" onClick={onClick} aria-label={label}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function WeekNavigator({ currentWeekStart, onWeekChange }: WeekNavigatorProps) {
  const today = new Date();
  const isCurrentWeek = isSameWeek(currentWeekStart, today, WEEK_OPTIONS);

  const weekStart = startOfWeek(currentWeekStart, WEEK_OPTIONS);
  const weekEnd = endOfWeek(currentWeekStart, WEEK_OPTIONS);
  const weekLabel = `${format(weekStart, 'dd MMM', { locale: ptBR })} - ${format(weekEnd, 'dd MMM yyyy', { locale: ptBR })}`;

  const goTo = (date: Date) => onWeekChange(startOfWeek(date, WEEK_OPTIONS));

  const handlePreviousWeek = () => goTo(subWeeks(currentWeekStart, 1));
  const handleNextWeek = () => goTo(addWeeks(currentWeekStart, 1));
  const handleGoToCurrentWeek = () => goTo(today);
  const handleLastWeek = () => goTo(subWeeks(today, 1));
  // "Mês Atual": semana da primeira segunda-feira do mês
  const handleCurrentMonth = () => goTo(new Date(today.getFullYear(), today.getMonth(), 1));

  return (
    <Card>
      <CardContent className="flex min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Anterior / semana / próxima */}
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:flex-none">
            <NavButton label="Semana anterior" onClick={handlePreviousWeek}>
              <ChevronLeft />
            </NavButton>

            <div className="flex min-w-0 flex-1 items-center justify-center gap-3 rounded-lg bg-muted px-4 py-2 sm:min-w-[280px]">
              <Calendar className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0 text-center">
                <p className="text-sm font-semibold text-foreground">{weekLabel}</p>
                {!isCurrentWeek && (
                  <p className="mt-0.5 text-xs text-muted-foreground">Você está vendo dados históricos</p>
                )}
              </div>
            </div>

            <NavButton label="Próxima semana" onClick={handleNextWeek}>
              <ChevronRight />
            </NavButton>
          </div>

          {/* Atalhos rápidos */}
          <div className="flex flex-wrap items-center gap-2">
            {!isCurrentWeek && (
              <Button variant="secondary" onClick={handleGoToCurrentWeek} title="Voltar para a semana atual">
                <RotateCcw className="text-primary" aria-hidden="true" />
                Esta Semana
              </Button>
            )}
            <Button variant="outline" onClick={handleLastWeek} title="Ir para a semana passada">
              Semana Passada
            </Button>
            <Button variant="outline" onClick={handleCurrentMonth} title="Primeira semana do mês atual">
              Mês Atual
            </Button>
          </div>
        </div>

        {!isCurrentWeek && (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
            <Info className="size-4 shrink-0" aria-hidden="true" />
            <span>
              Dados históricos de{' '}
              <span className="font-semibold text-foreground">
                {format(weekStart, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </span>
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
